/**
 * /embed/guide/:slug — iframe-friendly Program Guide for embedding on
 * 3rd-party sites (WordPress, Wix, custom HTML).
 *
 * Renders ONLY the channel grid + "Coming up next" — no header, nav,
 * sidebar, or footer chrome — so it slots cleanly into a station's own
 * site. Pulls from the SAME public endpoint as `/guide`.
 *
 * Usage:
 *   <iframe
 *     src="https://broadcastflow.ai/embed/guide/seductive-vibes-radio"
 *     width="100%" height="600" frameborder="0">
 *   </iframe>
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Tv, RefreshCw, ExternalLink } from "lucide-react";
import { fetchPublicGuide } from "@/lib/programGuideClient";

const TIME_SLOTS = [12, 13, 14, 15, 16, 17];

const COLOR_CLASSES = {
  news:  { bg: "bg-[#0d1e35]", text: "text-[#4a9eff]", border: "border-[#1e3050]" },
  music: { bg: "bg-[#0d2318]", text: "text-[#3ecf8e]", border: "border-[#1f3a2a]" },
  talk:  { bg: "bg-[#1a1230]", text: "text-[#a78bfa]", border: "border-[#2c1f4a]" },
  sport: { bg: "bg-[#241a08]", text: "text-[#f59e0b]", border: "border-[#3a2a10]" },
};

function ProgramCell({ program, channel }) {
  const c = COLOR_CLASSES[program.color || channel.color] || COLOR_CLASSES.news;
  return (
    <div
      data-testid={`embed-program-${program.id}`}
      className={`relative ${c.bg} ${c.text} border ${c.border} rounded-[5px] px-[7px] py-1.5 h-full overflow-hidden`}
      style={{ gridColumn: `span ${program.duration}` }}
      title={program.summary || program.description}
    >
      {program.live && (
        <span
          className="absolute top-1.5 right-1.5 h-[5px] w-[5px] rounded-full bg-[#ff4444]"
          style={{ animation: "embed-pulse 1.2s ease-in-out infinite" }}
        />
      )}
      <div className="text-[10px] font-medium leading-[1.3] truncate pr-3">{program.title}</div>
      <div className="text-[9px] opacity-70 mt-0.5">
        {String(program.start).padStart(2, "0")}:00 · {program.duration}h
      </div>
    </div>
  );
}

export default function GuideEmbed() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchPublicGuide(slug)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-refresh every 60s so embedded widgets stay current
  useEffect(() => {
    const t = setInterval(() => {
      fetchPublicGuide(slug).then(setData).catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, [slug]);

  // Fire-and-forget analytics ping (once per mount). Backend dedupes
  // by (station, domain, day, UA) so refresh/re-embed never inflates.
  useEffect(() => {
    if (!slug) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/program-guide/embed-view/${encodeURIComponent(slug)}`;
    try {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          referrer: document.referrer || "",
          ua: (navigator.userAgent || "").slice(0, 240),
        }),
      }).catch(() => { /* don't surface errors to embedded users */ });
    } catch { /* iframe-blocked envs */ }
  }, [slug]);

  const channels = data?.channels || [];
  const station = data?.station || null;
  const cols = `74px repeat(${TIME_SLOTS.length}, minmax(96px, 1fr))`;

  return (
    <div className="min-h-screen bg-[#0f1117] text-[#c8ccd8] font-sans antialiased p-4">
      <style>{`
        @keyframes embed-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.3; transform: scale(0.85); }
        }
        html, body, #root { background: #0f1117; }
      `}</style>

      {/* Compact branded header */}
      <div className="flex items-center justify-between mb-3" data-testid="embed-header">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-md bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center shrink-0">
            <Tv className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#4a9eff] font-semibold leading-none">
              Program Guide
            </div>
            <div className="text-[12px] font-semibold text-[#c8ccd8] truncate leading-tight mt-0.5">
              {station?.name || "Loading…"}
            </div>
          </div>
        </div>
        <a
          href={station ? `/r/${station.slug || station.station_id}` : "/"}
          target="_top"
          rel="noopener noreferrer"
          data-testid="embed-poweredby"
          className="text-[10px] text-[#4a5068] hover:text-[#c8ccd8] inline-flex items-center gap-1 transition shrink-0"
        >
          Powered by BroadcastFlow AI <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>

      {/* Body */}
      {loading && !data ? (
        <div className="bg-[#0a0d14] rounded-[10px] p-6 text-center" style={{ border: "0.5px solid #1e2130" }} data-testid="embed-loading">
          <RefreshCw className="h-4 w-4 text-[#4a9eff] animate-spin mx-auto mb-2" />
          <div className="text-[11px] text-[#4a5068]">Loading…</div>
        </div>
      ) : error ? (
        <div className="bg-[#0a0d14] rounded-[10px] p-6 text-center" style={{ border: "0.5px solid #1e2130" }} data-testid="embed-error">
          <div className="text-[12px] text-[#ff6b6b]">{error}</div>
        </div>
      ) : channels.length === 0 ? (
        <div className="bg-[#0a0d14] rounded-[10px] p-6 text-center" style={{ border: "0.5px dashed #1e2130" }} data-testid="embed-empty">
          <Tv className="h-5 w-5 text-[#4a9eff] mx-auto mb-2" />
          <div className="text-[12px] text-[#4a5068]">No schedule available.</div>
        </div>
      ) : (
        <section
          className="rounded-[10px] overflow-x-auto"
          style={{ border: "0.5px solid #1e2130" }}
          data-testid="embed-grid"
        >
          {/* Time row */}
          <div
            className="grid bg-[#0a0d14] px-1 py-1.5"
            style={{ gridTemplateColumns: cols, columnGap: "6px", borderBottom: "0.5px solid #1e2130" }}
          >
            <div className="text-[9px] uppercase tracking-wider text-[#4a5068] font-semibold px-1">Channel</div>
            {TIME_SLOTS.map((h) => (
              <div key={h} className="text-[9px] text-[#4a5068] font-medium px-1">
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          {/* Channel rows */}
          <div className="bg-[#0f1117]">
            {channels.map((ch, idx) => (
              <div
                key={ch.id}
                className="grid items-stretch"
                style={{
                  gridTemplateColumns: cols,
                  columnGap: "3px",
                  padding: "3px",
                  minHeight: "46px",
                  borderTop: idx === 0 ? "none" : "0.5px solid #1e2130",
                }}
                data-testid={`embed-channel-${ch.id}`}
              >
                <div
                  className="px-1.5 self-center bg-[#0a0d14] rounded-[5px] py-1.5"
                  style={{ borderRight: "0.5px solid #1e2130" }}
                >
                  <div className="text-[10px] font-medium text-[#c8ccd8] leading-tight truncate">{ch.name}</div>
                  <div className="text-[9px] text-[#4a5068] mt-0.5">{ch.type}</div>
                </div>
                {(ch.programs || []).map((p) => (
                  <ProgramCell key={p.id} program={p} channel={ch} />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
